(() => {
  if (window.SBScrollTargets) return;

  const obstructionSelector = '[data-sb-scroll-obstruction]';
  const targetSelector = '[data-sb-scroll-target]';
  const linkSelector = '[data-sb-scroll-link]';
  const topStackTolerance = 32;
  const correctionWindowMs = 4000;
  const settleDelayMs = 140;
  const maxTargetWaitMs = 2000;

  const getScrollTop = () => document.scrollingElement?.scrollTop ?? window.scrollY ?? 0;
  const getLayoutAbsoluteTop = (element) => {
    let absoluteTop = 0;
    let current = element;
    while (current instanceof HTMLElement) {
      absoluteTop += current.offsetTop;
      current = current.offsetParent;
    }
    return absoluteTop;
  };
  const getGap = () => {
    const value = Number.parseFloat(
      window.getComputedStyle(document.documentElement).getPropertyValue('--sb-space-96')
    );
    return Number.isFinite(value) ? value : 96;
  };

  const hasVisibleLayout = (element) => {
    if (!(element instanceof HTMLElement) || element.hidden) return false;
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden') {
      return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  };

  const isRenderedObstruction = (element) => {
    if (!hasVisibleLayout(element)) return false;
    return Number.parseFloat(window.getComputedStyle(element).opacity) !== 0;
  };

  const topStackBottomFromRects = (rects) => {
    let stackBottom = 0;
    let includedAnotherRect = true;
    while (includedAnotherRect) {
      includedAnotherRect = false;
      rects.forEach((rect) => {
        if (rect.bottom <= stackBottom || rect.top > stackBottom + topStackTolerance) return;
        stackBottom = Math.max(stackBottom, rect.bottom);
        includedAnotherRect = true;
      });
    }

    return Math.max(0, Math.min(window.innerHeight, stackBottom));
  };

  const getObstructions = () => Array.from(document.querySelectorAll(obstructionSelector)).filter(isRenderedObstruction);

  const getTopStackBottom = () => {
    const rects = getObstructions()
      .map((element) => element.getBoundingClientRect())
      .filter((rect) => rect.bottom > 0 && rect.top < window.innerHeight)
      .sort((a, b) => a.top - b.top);

    return topStackBottomFromRects(rects);
  };

  const getProspectiveStickyRect = (element, targetAbsoluteTop, topInsetOverride = null) => {
    let stickyContainer = element;
    let stickyPosition = '';
    while (stickyContainer && stickyContainer !== document.body) {
      const style = window.getComputedStyle(stickyContainer);
      if (style.position === 'sticky' || style.position === 'fixed') {
        stickyPosition = style.position;
        break;
      }
      stickyContainer = stickyContainer.parentElement;
    }
    if (!(stickyContainer instanceof HTMLElement) || stickyContainer === document.body) return null;

    const stickyStyle = window.getComputedStyle(stickyContainer);
    const stickyRect = stickyContainer.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    if (stickyPosition === 'fixed') {
      const top = stickyRect.top + (elementRect.top - stickyRect.top);
      return {
        top,
        bottom: top + elementRect.height,
      };
    }

    const stickyInset = Number.parseFloat(stickyStyle.top);
    if (!Number.isFinite(stickyInset)) return null;

    const scrollTop = getScrollTop();
    const stickyAbsoluteTop = stickyRect.top + scrollTop;
    const isCurrentlyStuck = Math.abs(stickyRect.top - stickyInset) <= 1;
    if (!isCurrentlyStuck && stickyAbsoluteTop > targetAbsoluteTop) return null;

    const effectiveStickyInset = Number.isFinite(topInsetOverride) ? topInsetOverride : stickyInset;
    const top = effectiveStickyInset + (elementRect.top - stickyRect.top);
    return {
      top,
      bottom: top + elementRect.height,
    };
  };

  const getProspectiveTopStackBottom = (
    targetAbsoluteTop,
    navigationDelta,
    { preserveHeaderState = false } = {}
  ) => {
    const obstructions = getObstructions();
    const stickyHeader = obstructions.find((element) => {
      const mode = `${element.dataset.stickyMode || ''}`.trim();
      return mode === 'always' || mode === 'scroll-up';
    }) || null;
    const stickyHeaderMode = `${stickyHeader?.dataset.stickyMode || ''}`.trim();
    const stickyHeaderRect = stickyHeader?.getBoundingClientRect() || null;
    const prospectiveStickyHeaderRect = stickyHeader
      ? getProspectiveStickyRect(stickyHeader, targetAbsoluteTop)
      : null;
    const stickyHeaderIsCurrentlyPinned = Boolean(
      stickyHeaderRect
      && prospectiveStickyHeaderRect
      && (
        stickyHeaderMode === 'always'
        || stickyHeader?.dataset.stickyState === 'active'
        || (
          stickyHeaderRect.bottom > 0
          && stickyHeaderRect.top < window.innerHeight
        )
      )
    );
    const stickyHeaderWillBeVisible = stickyHeaderMode === 'always'
      || (
        stickyHeaderMode === 'scroll-up'
        && (
          preserveHeaderState
            ? stickyHeaderIsCurrentlyPinned
            : (
              navigationDelta < -1
              || (Math.abs(navigationDelta) <= 1 && stickyHeaderIsCurrentlyPinned)
            )
        )
      );
    const stickyHeaderHeight = stickyHeaderWillBeVisible && prospectiveStickyHeaderRect
      ? prospectiveStickyHeaderRect.bottom - prospectiveStickyHeaderRect.top
      : 0;

    const rects = obstructions
      .map((element) => {
        if (element === stickyHeader) {
          if (!stickyHeaderWillBeVisible || !prospectiveStickyHeaderRect) return null;
          return prospectiveStickyHeaderRect;
        }

        const followsStickyHeader = stickyHeader
          && Boolean(stickyHeader.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
        return getProspectiveStickyRect(
          element,
          targetAbsoluteTop,
          followsStickyHeader ? stickyHeaderHeight : null
        );
      })
      .filter((rect) => rect && rect.bottom > 0 && rect.top < window.innerHeight)
      .sort((a, b) => a.top - b.top);

    return topStackBottomFromRects(rects);
  };

  const getReadingLine = () => getTopStackBottom() + getGap();
  const isManagedTarget = (target) => target instanceof HTMLElement && target.matches(targetSelector);
  const isTargetReady = (target) => {
    if (!isManagedTarget(target) || !hasVisibleLayout(target)) return false;
    return !target.closest('.shopify-section.sb-segment-hidden');
  };

  const resolveHashTarget = (hash) => {
    if (!hash || hash === '#') return null;
    let id = '';
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch (_) {
      id = hash.slice(1);
    }
    const target = document.getElementById(id);
    return isManagedTarget(target) ? target : null;
  };

  let activeNavigation = null;
  let scrollLockReleaseSequence = 0;
  let settledScrollLockReleaseSequence = 0;
  let scrollAnchorRestoreTimer = 0;
  let scrollAnchorInlineValue = null;
  let scrollAnchorInlinePriority = '';

  const suspendScrollAnchoring = () => {
    window.clearTimeout(scrollAnchorRestoreTimer);
    scrollAnchorRestoreTimer = 0;
    const root = document.documentElement;
    if (!(root instanceof HTMLElement)) return;
    if (scrollAnchorInlineValue === null) {
      scrollAnchorInlineValue = root.style.getPropertyValue('overflow-anchor');
      scrollAnchorInlinePriority = root.style.getPropertyPriority('overflow-anchor');
    }
    root.style.setProperty('overflow-anchor', 'none');
    scrollAnchorRestoreTimer = window.setTimeout(restoreScrollAnchoring, correctionWindowMs);
  };

  const restoreScrollAnchoring = () => {
    window.clearTimeout(scrollAnchorRestoreTimer);
    scrollAnchorRestoreTimer = 0;
    const root = document.documentElement;
    if (root instanceof HTMLElement && scrollAnchorInlineValue !== null) {
      if (scrollAnchorInlineValue) {
        root.style.setProperty('overflow-anchor', scrollAnchorInlineValue, scrollAnchorInlinePriority);
      } else {
        root.style.removeProperty('overflow-anchor');
      }
    }
    scrollAnchorInlineValue = null;
    scrollAnchorInlinePriority = '';
  };

  document.addEventListener('sb:page-scroll-lock:released', () => {
    const releaseSequence = ++scrollLockReleaseSequence;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (releaseSequence !== scrollLockReleaseSequence) return;
        settledScrollLockReleaseSequence = releaseSequence;
      });
    });
  });

  const isPageScrollReady = () => (
    document.body?.dataset.sbScrollLocked !== 'true'
    && settledScrollLockReleaseSequence === scrollLockReleaseSequence
  );

  const clearActiveNavigation = () => {
    if (!activeNavigation) return;
    if (activeNavigation.headerSyncSuspended) {
      window.SBHeaderNav?.setScrollSyncSuspended?.(false);
      activeNavigation.headerSyncSuspended = false;
    }
    window.clearTimeout(activeNavigation.settleTimer);
    window.clearTimeout(activeNavigation.expiryTimer);
    window.cancelAnimationFrame(activeNavigation.animationFrame);
    activeNavigation.resizeObserver?.disconnect();
    activeNavigation.mutationObserver?.disconnect();
    activeNavigation = null;
  };

  // Pursuit scrolling: the destination is recomputed EVERY frame while the
  // glide runs, so content lazy-loading during the traverse bends the
  // trajectory instead of leaving a stale endpoint that a later correction
  // has to teleport away from - the two-step jump this replaces.
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  const animateNavigationScroll = (navigation, durationMs) => {
    window.cancelAnimationFrame(navigation.animationFrame);
    const destinationOf = () => targetScrollTop(navigation.target, {
      preserveHeaderState: navigation.headerSyncSuspended,
    });
    if (durationMs <= 0) {
      window.scrollTo({ top: destinationOf(), behavior: 'auto' });
      scheduleSettledCorrection(navigation);
      return;
    }
    const startTop = getScrollTop();
    const startedAt = performance.now();
    const step = (now) => {
      if (activeNavigation !== navigation) return;
      const progress = Math.min(1, (now - startedAt) / durationMs);
      const nextTop = Math.round(startTop + (destinationOf() - startTop) * easeOutCubic(progress));
      window.scrollTo({ top: nextTop, behavior: 'auto' });
      if (progress < 1) {
        navigation.animationFrame = window.requestAnimationFrame(step);
      } else {
        scheduleSettledCorrection(navigation);
      }
    };
    navigation.animationFrame = window.requestAnimationFrame(step);
  };

  const targetScrollTop = (target, { preserveHeaderState = false } = {}) => {
    const absoluteTop = getLayoutAbsoluteTop(target);
    const currentReadingLine = getReadingLine();
    const navigationDelta = absoluteTop - currentReadingLine - getScrollTop();
    const prospectiveReadingLine = getProspectiveTopStackBottom(
      absoluteTop,
      navigationDelta,
      { preserveHeaderState }
    ) + getGap();
    const scroller = document.scrollingElement;
    const maxScrollTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
    return Math.max(0, Math.min(maxScrollTop, Math.round(absoluteTop - prospectiveReadingLine)));
  };

  const scheduleSettledCorrection = (navigation) => {
    if (activeNavigation !== navigation || !navigation.isSettling) return;
    window.clearTimeout(navigation.settleTimer);
    navigation.settleTimer = window.setTimeout(() => {
      if (activeNavigation !== navigation) return;
      if (!isTargetReady(navigation.target)) {
        clearActiveNavigation();
        return;
      }
      const nextTop = targetScrollTop(navigation.target, {
        preserveHeaderState: navigation.headerSyncSuspended,
      });
      const distance = Math.abs(getScrollTop() - nextTop);
      if (distance > 1) {
        // Late layout shift (an image finished loading after arrival): fold
        // the correction into a short glide sized to the distance instead of
        // teleporting, so it reads as motion rather than a jump.
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        animateNavigationScroll(
          navigation,
          reducedMotion ? 0 : Math.min(240, 90 + distance * 0.35)
        );
        return;
      }
      navigation.isSettling = false;
      navigation.target.dispatchEvent(
        new CustomEvent('sb:scroll-target:settled', {
          bubbles: true,
          detail: { target: navigation.target },
        })
      );
      clearActiveNavigation();
    }, settleDelayMs);
  };

  const beginCorrections = (navigation) => {
    const obstructions = Array.from(document.querySelectorAll(obstructionSelector));
    if (window.ResizeObserver) {
      navigation.resizeObserver = new ResizeObserver(() => {
        if (activeNavigation !== navigation) return;
        scheduleSettledCorrection(navigation);
      });
      navigation.resizeObserver.observe(document.body);
      obstructions.forEach((element) => navigation.resizeObserver.observe(element));
    }

    if (window.MutationObserver) {
      navigation.mutationObserver = new MutationObserver(() => {
        if (activeNavigation !== navigation) return;
        scheduleSettledCorrection(navigation);
      });
      obstructions.forEach((element) => {
        navigation.mutationObserver.observe(element, {
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'data-sticky-state'],
        });
      });
    }

    navigation.expiryTimer = window.setTimeout(() => {
      if (activeNavigation === navigation) clearActiveNavigation();
    }, correctionWindowMs);
  };

  const scrollTo = (
    targetOrHash,
    {
      behavior = 'smooth',
      updateHistory = false,
      historyMode = 'push',
      source = null,
    } = {}
  ) => {
    const target = typeof targetOrHash === 'string'
      ? resolveHashTarget(targetOrHash.startsWith('#') ? targetOrHash : `#${targetOrHash}`)
      : targetOrHash;
    if (!isManagedTarget(target)) return false;

    clearActiveNavigation();
    suspendScrollAnchoring();
    const navigation = {
      target,
      settleTimer: 0,
      expiryTimer: 0,
      resizeObserver: null,
      mutationObserver: null,
      isSettling: true,
      headerSyncSuspended: false,
    };
    activeNavigation = navigation;

    if (typeof window.SBHeaderNav?.setScrollSyncSuspended === 'function') {
      window.SBHeaderNav.setScrollSyncSuspended(true);
      navigation.headerSyncSuspended = true;
    }

    if (updateHistory) {
      const url = new URL(window.location.href);
      url.hash = target.id;
      const method = historyMode === 'replace' ? 'replaceState' : 'pushState';
      window.history[method]({}, '', url);
    }

    const startedAt = performance.now();
    const attempt = () => {
      if (activeNavigation !== navigation) return;
      if (!isPageScrollReady() || !isTargetReady(target)) {
        if (performance.now() - startedAt < maxTargetWaitMs) {
          window.requestAnimationFrame(attempt);
        } else {
          clearActiveNavigation();
        }
        return;
      }

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      beginCorrections(navigation);
      animateNavigationScroll(
        navigation,
        reducedMotion || behavior === 'auto' ? 0 : 550
      );
    };

    // Give the root anchoring override one frame to commit before preparation
    // can swap a large page segment (for example PDP learn-more -> reviews).
    // Without this boundary browsers may counter-scroll that swap to the
    // document end before our target pursuit begins.
    window.requestAnimationFrame(() => {
      if (activeNavigation !== navigation) return;
      target.dispatchEvent(
        new CustomEvent('sb:scroll-target:prepare', {
          bubbles: true,
          detail: { target, source },
        })
      );
      window.requestAnimationFrame(attempt);
    });
    return true;
  };

  const handleLocationHash = () => {
    const target = resolveHashTarget(window.location.hash);
    if (!target) return;
    scrollTo(target, { behavior: 'auto', source: 'history' });
  };
  let hashHandlingFrame = 0;
  let userScrollCancelledAt = Number.NEGATIVE_INFINITY;
  const scheduleHashHandling = () => {
    if (performance.now() - userScrollCancelledAt < 300) return;
    window.cancelAnimationFrame(hashHandlingFrame);
    hashHandlingFrame = window.requestAnimationFrame(handleLocationHash);
  };

  document.addEventListener('click', (event) => {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }

    const link = event.target.closest(linkSelector);
    if (!(link instanceof HTMLAnchorElement) || link.target === '_blank' || link.hasAttribute('download')) return;

    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) {
      return;
    }

    const target = resolveHashTarget(url.hash);
    if (!target) return;

    event.preventDefault();
    window.cancelAnimationFrame(hashHandlingFrame);
    hashHandlingFrame = 0;
    scrollTo(target, {
      updateHistory: true,
      historyMode: window.location.hash === url.hash ? 'replace' : 'push',
      source: link,
    });
  });

  window.addEventListener('hashchange', scheduleHashHandling);
  window.addEventListener('scroll', () => {
    if (activeNavigation?.isSettling) scheduleSettledCorrection(activeNavigation);
  }, { passive: true });
  if ('onscrollend' in window) {
    window.addEventListener('scrollend', () => {
      if (!activeNavigation?.isSettling) return;
      scheduleSettledCorrection(activeNavigation);
    }, { passive: true });
  }
  const cancelForUserScroll = () => {
    userScrollCancelledAt = performance.now();
    window.cancelAnimationFrame(hashHandlingFrame);
    hashHandlingFrame = 0;
    clearActiveNavigation();
    restoreScrollAnchoring();
  };
  window.addEventListener('wheel', cancelForUserScroll, { passive: true });
  window.addEventListener('touchstart', cancelForUserScroll, { passive: true });
  window.addEventListener('pointerdown', cancelForUserScroll, { passive: true });
  window.addEventListener('keydown', (event) => {
    if (['ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End', ' '].includes(event.key)) {
      cancelForUserScroll();
    }
  });
  window.addEventListener('load', () => {
    if (activeNavigation?.isSettling) scheduleSettledCorrection(activeNavigation);
  }, { once: true });

  window.SBScrollTargets = {
    getLayoutAbsoluteTop,
    getReadingLine,
    getTopStackBottom,
    resolveHashTarget,
    scrollTo,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleHashHandling, { once: true });
  } else {
    scheduleHashHandling();
  }
})();
